import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import { Autocomplete, TextField, Chip, CircularProgress, Grid, Select, FormControl, MenuItem, Typography, FormHelperText } from "@mui/material";
import { debounce } from "@mui/material/utils";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Button, COLORS, Modal } from "abs-ui-components";
import { useNavigate } from "react-router-dom";
import { MDBCard, MDBCardBody } from "mdbreact";
import axiosHttp from "../../../../services/interceptor";
import { emitToasterMsg } from "../../../../services/toasterUtility";
import BreadCrumb from "../../../../common/breadCrumb/BreadCrumb";
import { getAllCountries } from "../../../../services/PortCodeService";
import UnsatisfactoryCIIRatingExportList from "./UnsatisfactoryCIIRatingExportList";
import { ClaimsContext } from "../../../../services/ClaimsService";
import { ClaimType } from "../../../../constants/claims";
import { FeatureType } from "../../../../constants/FeatureToggles";
import "./UnsatisfactoryCIIRatingExport.scss";

const baseURL = process.env.REACT_APP_BASE_URL;

const WcnAutocomplete = ({ value = [], onChange }) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [defaultOptions, setDefaultOptions] = useState([]);

    const loadOptions = async (searchText = "") => {
        setLoading(true);

        try {
            const response = await axiosHttp.post(
                `${baseURL}api/Wcn/SearchWcn`,
                {
                    searchKey: searchText,
                }
            );

            const formattedOptions =
                response?.data?.map((item) => ({
                    label: `${item.WcnNumber} - ${item.CompanyName}`,
                    value: item.WcnNumber,
                    wcnMstId: item.WcnMstId,
                })) || [];

            if (searchText === "") {
                setDefaultOptions(formattedOptions);
            }

            setOptions(formattedOptions);
        } catch (error) {
            emitToasterMsg("error", "Error", error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadOptions("");
    }, []);

    const debouncedSearch = useMemo(
        () =>
            debounce((searchText) => {
                loadOptions(searchText);
            }, 400),
        []
    );

    useEffect(() => {
        return () => {
            debouncedSearch.clear();
        };
    }, [debouncedSearch]);

    const handleBlur = () => {
        setInputValue("");
        setOptions(defaultOptions);
    };

    return (
        <Autocomplete
            multiple
            options={options}
            value={value}
            loading={loading}
            inputValue={inputValue}
            filterOptions={(x) => x} // Server-side filtering
            disableCloseOnSelect
            isOptionEqualToValue={(option, value) =>
                option.value === value.value
            }
            getOptionLabel={(option) => option.label}
            onBlur={handleBlur}
            onChange={(event, newValue) => {
                onChange(newValue);
                if (!newValue) {
                    setInputValue("");
                    loadOptions("");
                }
            }}
            onInputChange={(event, newInputValue, reason) => {
                setInputValue(newInputValue);

                if (reason === "input") {
                    debouncedSearch(newInputValue);
                }
            }}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                    <Chip
                        label={option.value}
                        {...getTagProps({ index })}
                    />
                ))
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder="Search WCN or Company"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? (
                                    <CircularProgress
                                        color="inherit"
                                        size={18}
                                    />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

const UnsatisfactoryCIIRatingExportPage = () => {
    const { userHasClaim, userHasFeature, userFeaturesAreLoaded } = useContext(ClaimsContext);
    const [imoVesselTypes, setImoVesselTypes] = useState([]);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [refreshTable, setRefreshTable] = React.useState(false);
    const isAdmin = userHasClaim(ClaimType.VIEW_ADMIN);
    const canSeeContainer = isAdmin && userFeaturesAreLoaded && userHasFeature(FeatureType.UnsatisfactoryCiiExport);
    const navigate = useNavigate();

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        getValues,
    } = useForm({
        defaultValues: {
            exportScope: "All Vessels",
            reportingYear: "",
            flag: "",
            wcns: [],
            imoVesselTypes: [],
        },
    });
    const exportScope = useWatch({
        control,
        name: "exportScope",
    });

    const toggleRefreshTable = () => {
        setRefreshTable((prev) => !prev);
    };

    const reportingYears = Array.from(
        { length: 10 },
        (_, index) => 2027 - index
    );

    const breadcrumb = useMemo(
        () => [
            {
                label: "Admin",
                url: "",
                navId: "admin_navid",
            },
            {
                label: "Unsatisfactory CII Ratings Vessel Export",
                url: "",
                navId: "unsatisfactory_cii_rating_export_navid",
            },
        ],
        []
    );

    useEffect(() => {
        const fetchImoVesselTypes = async () => {
            try {
                const response = await axiosHttp.get(
                    `${baseURL}api/MasterData/GetImoVesselType`
                );
                setImoVesselTypes(response?.data.map((item) => ({
                    label: item.Value,
                    value: item.ImoVesselTypeMstId,
                }))
                    || []);
            }
            catch (error) {
                emitToasterMsg("error", "Error", error.message);
            }
        };

        fetchImoVesselTypes();
        loadCountries("");
    }, []);

    const loadCountries = useCallback(async (searchInputValue = "") => {
        setLoading(true);

        try {
            const payLoad = {
                searchKey: searchInputValue,
            };

            const response = await getAllCountries(payLoad);

            const formattedCountryOptions =
                response
                    ?.slice(0, 10)
                    ?.map((item) => ({
                        label: `${item.CountryName} (${item.CountryCode})`,
                        value: item.CountryName,
                    })) || [];

            setOptions(formattedCountryOptions);
        } catch (error) {
            emitToasterMsg("error", "Error", error.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const debouncedSearch = useMemo(
        () =>
            debounce((searchText) => {
                loadCountries(searchText);
            }, 400),
        []
    );

    useEffect(() => {
        return () => {
            debouncedSearch.clear();
        };
    }, [debouncedSearch]);

    const resetFilters = () => {
        reset();
        setInputValue("");
        loadCountries("");
    }

    const generateExport = async (formValues) => {
        setIsLoading(true)
        try {
            const payload = {
                isAllVessels: formValues.exportScope === "All Vessels",
                flag: formValues.flag,
                wcnMstIds: formValues.wcns?.map(
                    (wcn) => wcn.wcnMstId
                ) || [],
                imoVesselTypeMstIds:
                    formValues.imoVesselTypes?.map(
                        (type) => type.value
                    ) || [],
            };

            console.log("Payload for Export:", payload);

            const response = await axiosHttp.post(
                `${baseURL}api/Dashboard/CreateUnsatisfactoryCiiExportRequest`,
                payload.isAllVessels
                    ? payload
                    : {
                        ...payload,
                        reportingYear: formValues.reportingYear,
                    }
            );

            console.log("Export Response:", response);

            if (response.status === 200) {
                emitToasterMsg(
                    "success",
                    "Export Request",
                    "Export request submitted. Refresh to check status and download when ready."
                );

                toggleRefreshTable();
                resetFilters();
            }
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "An error occurred while generating the export.";

            console.error(
                "Error generating export request:",
                error
            );

            emitToasterMsg(
                "error",
                "Export Request Error",
                errorMessage
            );
        }
        finally {
            setIsLoading(false)
        }
    };

    const handleOk = () => {
        navigate("/dashboard");
    };

    return (
        canSeeContainer ? <section className="p-4 unsatisfactory-cii-rating-export-page">
            <BreadCrumb items={breadcrumb} />

            <MDBCard className="my-4">
                <MDBCardBody>
                    <Grid container spacing={2}>
                        {/* Export Scope */}
                        <Grid item xs={12} sm={6} md={4} lg={2.4}>
                            <Controller
                                name="exportScope"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth size="small">
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                mb: 0.5,
                                                fontWeight: 500,
                                                fontSize: "14px"
                                            }}
                                        >
                                            Export Scope
                                        </Typography>

                                        <Select
                                            {...field}
                                            className="p-1 rounded-2"
                                            onChange={(e) => {
                                                const value = e.target.value;

                                                field.onChange(value);

                                                // Reporting Year is only relevant
                                                // when Reporting Year scope is selected
                                                if (value === "All Vessels") {
                                                    setValue("reportingYear", "");
                                                }
                                            }}
                                            MenuProps={{
                                                disableScrollLock: true,
                                            }}
                                        >
                                            <MenuItem value="All Vessels">
                                                All Vessels
                                            </MenuItem>

                                            <MenuItem value="Reporting Year">
                                                Reporting Year
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        {/* Reporting Year */}
                        <Grid item xs={12} sm={6} md={4} lg={2.4}>
                            <Controller
                                name="reportingYear"
                                control={control}
                                rules={{
                                    validate: (value) => {
                                        if (
                                            exportScope === "Reporting Year" &&
                                            !value
                                        ) {
                                            return "Please select Reporting Year";
                                        }

                                        return true;
                                    },
                                }}
                                render={({ field, fieldState: { error } }) => (
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        error={Boolean(error)}
                                    >
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                mb: 0.5,
                                                fontWeight: 500,
                                                fontSize: "14px"
                                            }}
                                        >
                                            Reporting Year

                                            {exportScope === "Reporting Year" && (
                                                <span style={{ color: "red" }}>
                                                    {" "}*
                                                </span>
                                            )}
                                        </Typography>

                                        <Select
                                            {...field}
                                            displayEmpty
                                            className="p-1"
                                            disabled={
                                                exportScope !== "Reporting Year"
                                            }
                                            value={field.value || ""}
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                            renderValue={(selected) => {
                                                if (!selected) {
                                                    return (
                                                        <span
                                                            style={{
                                                                color: "#9e9e9e",
                                                            }}
                                                        >
                                                            Select Reporting Year
                                                        </span>
                                                    );
                                                }

                                                return selected;
                                            }}
                                            MenuProps={{
                                                disableScrollLock: true,
                                            }}
                                        >

                                            {reportingYears.map((year) => (
                                                <MenuItem
                                                    key={year}
                                                    value={year}
                                                >
                                                    {year}
                                                </MenuItem>
                                            ))}
                                        </Select>

                                        {error && (
                                            <FormHelperText>
                                                {error.message}
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        {/* Flag */}
                        <Grid item xs={12} sm={6} md={4} lg={2.4}>
                            <Typography
                                variant="body1"
                                sx={{
                                    mb: 0.5,
                                    fontWeight: 500,
                                    fontSize: "14px"
                                }}
                            >
                                Flag
                            </Typography>

                            <Controller
                                name="flag"
                                control={control}
                                render={({ field }) => (
                                    <Autocomplete
                                        options={options}
                                        value={
                                            options.find(
                                                (option) =>
                                                    option.value === field.value
                                            ) || null
                                        }
                                        loading={loading}
                                        inputValue={inputValue}
                                        filterOptions={(x) => x}
                                        getOptionLabel={(option) =>
                                            option?.label || ""
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option.value === value.value
                                        }
                                        onChange={(event, newValue) => {
                                            field.onChange(
                                                newValue?.value || ""
                                            );
                                        }}
                                        onInputChange={(
                                            event,
                                            newInputValue,
                                            reason
                                        ) => {
                                            if (
                                                field.value &&
                                                reason === "input"
                                            ) {
                                                return;
                                            }

                                            setInputValue(newInputValue);

                                            if (reason === "input") {
                                                debouncedSearch(newInputValue);
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                placeholder="Search Country"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    readOnly: Boolean(field.value),
                                                    endAdornment: (
                                                        <>
                                                            {loading && (
                                                                <CircularProgress
                                                                    color="inherit"
                                                                    size={18}
                                                                />
                                                            )}

                                                            {
                                                                params.InputProps
                                                                    .endAdornment
                                                            }
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>

                        {/* WCN */}
                        <Grid item xs={12} sm={6} md={6} lg={2.4}>
                            <Typography
                                variant="body1"
                                sx={{
                                    mb: 0.5,
                                    fontWeight: 500,
                                    fontSize: "14px"
                                }}
                            >
                                WCN
                            </Typography>
                            <Controller
                                name="wcns"
                                control={control}
                                render={({ field }) => (
                                    <WcnAutocomplete
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={6} lg={2.4}>
                            <Typography
                                variant="body1"
                                sx={{
                                    mb: 0.5,
                                    fontWeight: 500,
                                    fontSize: "14px"
                                }}
                            >
                                IMO Vessel Type
                            </Typography>

                            <Controller
                                name="imoVesselTypes"
                                control={control}
                                render={({ field }) => (
                                    <Autocomplete
                                        multiple
                                        options={imoVesselTypes}
                                        value={field.value || []}
                                        onChange={(event, newValue) => {
                                            field.onChange(newValue);
                                        }}
                                        disableCloseOnSelect
                                        isOptionEqualToValue={(option, value) =>
                                            option.value === value.value
                                        }
                                        getOptionLabel={(option) =>
                                            option?.label || ""
                                        }
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    label={option.label}
                                                    size="small"
                                                    {...getTagProps({ index })}
                                                    key={option.value}
                                                />
                                            ))
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                placeholder="Select IMO Vessel Type"
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>

                        <div className="d-flex  my-4 mx-3">
                            <Button
                                className="export-btn"
                                onClick={handleSubmit(generateExport)}
                            >
                                {isLoading ? "Generating Export..." : "Generate Export"}
                            </Button>

                            <Button
                                className="ml-3 p-2 br-8 rounded-2 border-0 reset-btn"
                                variant="secondary"
                                onClick={resetFilters}
                            >
                                Reset
                            </Button>
                        </div>

                    </Grid>
                </MDBCardBody>
            </MDBCard>

            <UnsatisfactoryCIIRatingExportList refresh={refreshTable} />
        </section> :
            <div>
                <Modal
                    isOpen={!canSeeContainer}
                    modalHeader
                    padding={"70px"}
                    headerHeight={"50px"}
                    showFooter
                    footer={
                        <div className="d-flex justify-content-end fixed-action-btns p-4">
                            <Button
                                className="modal-delete-btn"
                                onClick={handleOk}
                                primaryColor={COLORS.RED.DARK}
                                primaryBackgroundColor={COLORS.RED.LIGHT}
                            >
                                Ok
                            </Button>
                        </div>
                    }
                >
                    <div>You are not authorized to view this.</div>
                </Modal>
            </div>
    )
}

export default UnsatisfactoryCIIRatingExportPage;
